import { SignalGraphD3 } from './signal-graph.d3';
import type { SafeAny } from '@/common/types';
import { NumberValue, axisLeft, line, utcFormat, select, Selection } from 'd3';
import type { GraphPoint } from '@/common/ui';
import { applyArtifactBadData, i18n } from '@/common/utils';
import { buildEventTooltipContent } from '@/core/helpers';
const { t } = i18n.global;

export const positionLabels = {
  0: 'Prone',
  90: 'Right',
  180: 'Supine',
  270: 'Left',
  360: 'Prone',
  450: 'Upright'
};

export class PositionSignalGraphD3 extends SignalGraphD3 {
  protected getMinAndMax(): { min: number; max: number } {
    return {
      min: 0,
      max: 450
    };
  }

  protected getYTickFormat(d: NumberValue): string {
    return positionLabels[d as keyof typeof positionLabels];
  }

  protected updateLine(color: string): this {
    if (!this.yScale) return this;

    this.graphGroup.selectAll('path.single-line').remove();
    this.yAxisGroup.selectAll('.ecg-flip-marker').remove();

    const drawLine = line<GraphPoint<SafeAny>>()
      .x((d: GraphPoint<SafeAny>) => this.xScale(d.x))
      .defined((d: GraphPoint<SafeAny>) => d.y !== null)
      .y((d: GraphPoint<SafeAny>) => this.yScale(d.y));

    if (Array.isArray(this.data[0].y)) {
      this.data = this.convertMultipleYValuesIntoLine(this.mappedData);
    }

    if (this.artifactEvents?.length) {
      this.data = applyArtifactBadData(this.data, this.artifactEvents);
    }

    const lineColor = this.simpleSignal ? this.simpleSignalColor : color;

    this.graphGroup
      .append<SVGPathElement>('path')
      .datum(this.data)
      .attr('class', 'single-line')
      .attr('fill', 'none')
      .attr('stroke', lineColor)
      .attr('d', drawLine);

    const badDataPoints = this.data.filter((d) => d.y === null);

    const badMarkers = this.graphGroup.selectAll('circle.bad-marker').data(badDataPoints);

    badMarkers
      .enter()
      .append('circle')
      .classed('bad-marker', true)
      .merge(badMarkers as SafeAny)
      .attr('cx', (d) => this.xScale(d.x))
      .attr('cy', () => this.yScale(this.yScale.domain()[0]))
      .attr('r', 1)
      .attr('fill', 'darkgray');

    badMarkers.exit().remove();

    this.updatePositionLabels();
    this.drawBadDataLines(this.data);

    if (this.ecgPolarityInversion) {
      this.renderEcgPolarityMarker();
    }

    return this;
  }

  private renderEcgPolarityMarker(): void {
    const markerGroup = this.yAxisGroup.append('g').attr('class', 'ecg-flip-marker');

    const isLowerHeight = this.svgSizes.height < 30;

    let xPosition = isLowerHeight ? -65 : -75;
    let yPosition = isLowerHeight ? 14 : 18;

    if (this.svgSizes.height >= 60) {
      xPosition -= 10;
      yPosition += 7;
    }

    // Draw the red exclamation mark
    markerGroup
      .append('text')
      .attr('x', xPosition)
      .attr('y', yPosition)
      .attr('fill', 'red')
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .text('!');

    // Tooltip
    const tooltip = select('body')
      .append('div')
      .attr('class', 'ecg-tooltip')
      .style('position', 'absolute')
      .style('display', 'none')
      .style('background', '#fff')
      .style('border', '1px solid #ccc')
      .style('padding', '4px 8px')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .text(t('sensorLikelyWornUpsideDownDescription'));

    this.attachMarkerTooltipHandlers(markerGroup, tooltip);
  }

  private attachMarkerTooltipHandlers(
    selection: Selection<SVGGElement, unknown, HTMLElement, any>,
    tooltip: Selection<HTMLDivElement, unknown, HTMLElement, any>
  ): void {
    selection
      .on('mouseenter', (event: MouseEvent) => {
        tooltip
          .style('display', 'block')
          .style('left', `${event.pageX + 10}px`)
          .style('top', `${event.pageY + 10}px`);
      })
      .on('mousemove', (event: MouseEvent) => {
        tooltip.style('left', `${event.pageX + 10}px`).style('top', `${event.pageY + 10}px`);
      })
      .on('mouseleave', () => {
        tooltip.style('display', 'none');
      });
  }

  protected updateYAxis() {
    if (!this.yAxisGroup || !this.yScale) return;

    this.yAxisGroup
      .attr(
        'transform',
        `translate(${
          this.graphSettings.chartLayout.margins.left * 2 - 30
        }, ${this.graphSettings.chartLayout.margins.top})`
      )
      .raise()
      .call(
        axisLeft(this.yScale)
          .tickFormat((d: any) => this.getYTickFormat(d))
          .tickValues(Object.keys(positionLabels).map(Number))
          .tickSizeOuter(0)
          .tickSizeInner(0)
      );

    this.yAxisGroup
      .selectAll('.tick text')
      .style('font-size', this.svgSizes.height < 45 ? '6px' : '8px')
      .style('color', 'grey');
  }

  private updatePositionLabels() {
    this.graphGroup.selectAll('text.position-overlay-label').remove();
    const data = this.getListOfLabels(this.data, 35);
    const { max } = this.getMinAndMax();

    const labels = this.graphGroup.selectAll('text.label').data(data);

    labels
      .enter()
      .append('text')
      .classed('label', true)
      .merge(labels as SafeAny)
      .attr('x', (d) => this.xScale(d.x))
      .attr('y', (d) => this.yScale(d.y))
      .attr('transform', (d) => `translate(0, ${d.y < max ? -5 : 10})`)
      .attr('text-anchor', 'start')
      .text((d) => positionLabels[d.combinedY ?? d.y]);

    labels.exit().remove();
  }

  protected buildTooltipContent(signalData: GraphPoint<SafeAny>): string {
    const { t } = i18n.global;

    const time = utcFormat('%H:%M:%S')(new Date(signalData.x * 1000));

    const isBadData = signalData.y == null;
    const labelKey = (signalData.combinedY || signalData.y) as keyof typeof positionLabels;
    const label =
      !isBadData && labelKey in positionLabels ? positionLabels[labelKey] : t('artifact');

    let tooltipHtml = `
      <p>
        <span class='text-capitalize'>${t('time')}</span>:
        <strong>${time}</strong>
      </p>
      <p>
        <span class='text-capitalize'>${this.title}</span>:
        <strong class='text-capitalize'>${t(label.toLowerCase())}</strong>
      </p>
    `;

    if (this.activeEvent) {
      tooltipHtml += buildEventTooltipContent(this.activeEvent);
    }

    return tooltipHtml.trim();
  }
}
